import React from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { ArticleJsonLd } from "next-seo";
import Image from "next/image";
import { type Blog } from "lib/sanity";
import { Section } from "components/section";
import { Breadcrumbs } from "ui/breadcrumbs";
import { Rule } from "ui/rule";
import { formatDateWithYear } from "utils";
import { blogBlurDataUrl } from "../../constants/blur-data-urls";

type BlogArticleHeroProps = {
  metaTitle: string;
  metaDescription: string;
} & Blog;
export function BlogArticleHero({
  slug,
  category,
  title,
  excerpt,
  publishedAt,
  authors,
  imageUrl,
  imageAlt,
  metaTitle,
  metaDescription,
}: BlogArticleHeroProps) {
  return (
    <Section my={0}>
      <Breadcrumbs
        fontFamily="inter"
        mt={{ base: "tinier", md: "smaller" }}
        crumbs={[
          { title: "Blog", href: "/blog" },
          { title: category.name, href: `/blog/category/${category.slug}/page/1` },
        ]}
      />
      <Flex
        align="center"
        gap={{ base: "bigger", md: "biggest" }}
        direction={{ base: "column", lg: "row" }}
        mt={{ base: "small", md: "big" }}
        mb={{ base: "bigger", md: "biggest" }}
      >
        <Flex fontFamily="inter" direction="column" gap="small" flex={1}>
          <Text as="h1" textStyle="article.h3" my={0}>
            {title}
          </Text>

          {excerpt && (
            <Text as="h2" color="new.gray.900" textStyle="article.p" mb={0} mt={0}>
              {excerpt}
            </Text>
          )}

          <ArticleJsonLd
            type="Blog"
            url={`https://hightouch.com/blog/${slug}`}
            title={metaTitle}
            images={[`${imageUrl}?w=200&h=200&fit=crop&crop=entropy`]}
            datePublished={publishedAt}
            dateModified={publishedAt}
            authorName={authors[0].name}
            description={metaDescription}
          />

          <Flex color="brand.textSecondary.light" gap="tiny" mt="tiny" fontSize="brand.sm" wrap="wrap">
            <Flex align="center" gap="tinier">
              <Text>{authors.map(({ name }) => name).join(", ")}</Text>
            </Flex>{" "}
            /<Text>{formatDateWithYear(publishedAt)}</Text>
          </Flex>
        </Flex>

        <Box width="100%" flex={1} height="auto">
          <Box
            pos="relative"
            width="100%"
            borderRadius="base"
            border="1px"
            borderColor="new.gray.150"
            maxW="600px"
            mx="auto"
            overflow="hidden"
          >
            <Image
              src={`${imageUrl}?w=2000`}
              alt={imageAlt ?? title + "."}
              placeholder="blur"
              blurDataURL={blogBlurDataUrl}
              sizes="100%"
              width={600}
              height={300}
              style={{
                objectFit: "contain",
                width: "100%",
                height: "auto",
              }}
            />
          </Box>
        </Box>
      </Flex>
      <Rule />
    </Section>
  );
}
